<template>
  <div class="scheduling-investigation-page">
    <section class="investigation-console q-mb-md">
      <div class="row gutter-md items-end">
        <div class="col-md-9 col-xs-12">
          <q-field
            :error="Boolean(validationError)"
            :error-label="validationError"
            stack-label="Inquiry IDs"
          >
            <q-input
              v-model="inquiryIdsInput"
              type="textarea"
              :rows="2"
              placeholder="18421, 18439, 18502"
              @keyup.ctrl.enter="investigate"
              @keyup.meta.enter="investigate"
            />
          </q-field>
          <div class="input-hint">
            Separate IDs with commas, spaces, or new lines. Press Ctrl/⌘ + Enter to investigate.
          </div>
        </div>
        <div class="col-md-3 col-xs-12">
          <q-btn
            class="full-width investigate-button"
            color="primary"
            icon="search"
            label="Investigate"
            no-caps
            :loading="loading"
            @click="investigate"
          />
        </div>
      </div>
    </section>

    <template v-if="hasResult">
      <section
        v-if="result.missing_inquiry_ids.length"
        class="missing-banner q-mb-md"
      >
        <q-icon name="warning" />
        No inquiry was found for: {{ result.missing_inquiry_ids.join(', ') }}
      </section>

      <section
        v-if="result.diagnostics.length"
        class="diagnostics-panel q-mb-md"
      >
        <div class="section-label">
          Diagnostic signals
        </div>
        <div class="diagnostic-grid">
          <div
            v-for="(diagnostic, index) in result.diagnostics"
            :key="`${diagnostic.type}-${diagnostic.entity_type}-${diagnostic.entity_id}-${index}`"
            class="diagnostic-card"
            :class="`diagnostic-${diagnostic.severity}`"
          >
            <q-icon :name="diagnostic.severity === 'error' ? 'error' : 'warning'" />
            <span>{{ diagnostic.summary }}</span>
          </div>
        </div>
      </section>

      <section class="timeline-panel">
        <div class="timeline-toolbar">
          <div>
            <div class="section-label">
              Event timeline
            </div>
            <div class="timeline-count">
              Showing {{ filteredEvents.length }} of {{ result.events.length }} events
            </div>
            <div
              v-if="attributedInquiryIds.length"
              class="inquiry-key"
            >
              <span
                v-for="inquiryId in attributedInquiryIds"
                :key="inquiryId"
                class="inquiry-key-item"
              >
                <i :style="inquiryColorStyle(inquiryId)" />
                Inquiry #{{ inquiryId }}
              </span>
              <span
                v-if="hasSharedEvents"
                class="inquiry-key-item"
              >
                <i class="shared-key" />
                Shared
              </span>
            </div>
          </div>
          <div class="filter-control">
            <div class="timeline-options">
              <q-btn
                dense
                flat
                no-caps
                icon="content_copy"
                label="Copy all as Markdown"
                class="copy-markdown-button"
                @click="copyMarkdown"
              />
              <q-toggle
                v-model="onlyAnomalies"
                label="Only failures and anomalies"
                color="negative"
              />
            </div>
            <q-select
              v-model="selectedEventTypes"
              :options="result.event_types"
              multiple
              chips
              filter
              stack-label="Event types"
            />
            <div class="filter-actions">
              <button
                type="button"
                @click="selectAllEventTypes"
              >
                Select all
              </button>
              <span>/</span>
              <button
                type="button"
                @click="clearEventTypes"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="filteredEvents.length"
          class="event-list"
        >
          <article
            v-for="event in filteredEvents"
            :key="event.id"
            class="event-row"
            :class="[
              `event-${event.type.replace('.', '-')}`,
              eventSeverity(event) ? `event-${eventSeverity(event)}` : '',
              {
                'is-expanded': isExpanded(event.id),
                'is-inquiry-specific': event.inquiry_ids && event.inquiry_ids.length === 1,
                'is-shared-event': event.inquiry_ids && event.inquiry_ids.length > 1,
              },
            ]"
            :style="eventInquiryStyle(event)"
          >
            <button
              type="button"
              class="event-header"
              :aria-expanded="isExpanded(event.id) ? 'true' : 'false'"
              @click="toggleEvent(event.id)"
            >
              <span class="event-marker" />
              <span class="event-time">{{ event.occurred_at }}</span>
              <span class="event-separator">—</span>
              <span class="event-summary">{{ event.summary }}</span>
              <span class="event-type">{{ eventTypeLabel(event.type) }}</span>
              <q-icon
                class="event-toggle"
                :name="isExpanded(event.id) ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
              />
            </button>
            <div
              v-if="isExpanded(event.id)"
              class="event-details"
            >
              <div class="details-column">
                <div class="details-label">
                  Event data
                </div>
                <pre>{{ formatJson(event.details) }}</pre>
              </div>
              <div class="details-column">
                <div class="details-label">
                  Related context · current state
                </div>
                <pre>{{ formatJson(event.context) }}</pre>
              </div>
            </div>
          </article>
        </div>
        <div
          v-else
          class="empty-state"
        >
          <q-icon name="filter_list" />
          <strong>No events match the current filters.</strong>
          <span v-if="onlyAnomalies">No failures or anomalies match the selected event types.</span>
          <span v-else-if="result.events.length">Select one or more event types to restore the timeline.</span>
          <span v-else>No dated events were found for this inquiry graph.</span>
        </div>
      </section>
    </template>

    <section
      v-else-if="apiError"
      class="error-state"
    >
      <q-icon name="error_outline" />
      <div>
        <strong>Investigation failed</strong>
        <p>{{ apiError }}</p>
      </div>
    </section>

    <section
      v-else
      class="initial-state"
    >
      <div class="initial-orbit">
        <q-icon name="device_hub" />
      </div>
      <strong>Start with an inquiry ID</strong>
      <span>
        The graph will reveal scheduling attempts and Nylas responses, shared schedules, related leads,
        notifications, showing profiles and their assignee pools, tours, device access code attempts, and Showami logs.
      </span>
    </section>
  </div>
</template>

<script>
export default {
  name: 'SchedulingInvestigation',
  data() {
    return {
      apiError: null,
      expandedEventIds: [],
      inquiryIdsInput: '',
      loading: false,
      onlyAnomalies: false,
      result: null,
      selectedEventTypes: [],
      validationError: null,
    };
  },
  computed: {
    hasResult() {
      return this.result !== null;
    },
    filteredEvents() {
      if (!this.result) return [];
      return this.result.events.filter((event) => (
        this.selectedEventTypes.includes(event.type)
        && (!this.onlyAnomalies || event.is_anomaly)
      ));
    },
    attributedInquiryIds() {
      if (!this.result) return [];
      return [...new Set(this.result.events.flatMap((event) => event.inquiry_ids || []))]
        .sort((firstId, secondId) => firstId - secondId);
    },
    hasSharedEvents() {
      return this.result && this.result.events.some((event) => (
        event.inquiry_ids && event.inquiry_ids.length > 1
      ));
    },
  },
  watch: {
    '$route.params.inquiry_ids': {
      handler(inquiryIds) {
        const normalizedInquiryIds = inquiryIds || '';
        const loadedInquiryIds = this.result
          ? this.result.requested_inquiry_ids.join(',')
          : '';

        if (normalizedInquiryIds === loadedInquiryIds) return;
        if (!normalizedInquiryIds) {
          this.inquiryIdsInput = '';
          this.result = null;
          this.apiError = null;
          this.validationError = null;
          return;
        }

        this.inquiryIdsInput = normalizedInquiryIds;
        this.investigate();
      },
    },
  },
  created() {
    const inquiryIds = this.routeInquiryIds();

    if (inquiryIds) {
      this.inquiryIdsInput = inquiryIds;
      this.investigate();
    }
  },
  methods: {
    routeInquiryIds() {
      if (this.$route.params.inquiry_ids) return this.$route.params.inquiry_ids;
      if (Array.isArray(this.$route.query.inquiry_ids)) {
        return this.$route.query.inquiry_ids.join(',');
      }
      return this.$route.query.inquiry_ids;
    },
    parseInquiryIds() {
      const tokens = this.inquiryIdsInput
        .split(/[\s,]+/)
        .map((token) => token.trim())
        .filter(Boolean);

      if (!tokens.length) {
        throw new Error('Enter at least one inquiry ID.');
      }
      if (tokens.some((token) => !/^\d+$/.test(token) || Number(token) < 1)) {
        throw new Error('Inquiry IDs must be positive whole numbers.');
      }

      const inquiryIds = [...new Set(tokens.map((token) => Number(token)))];
      if (inquiryIds.length > 25) {
        throw new Error('Enter no more than 25 inquiry IDs.');
      }

      return inquiryIds;
    },
    async investigate() {
      this.validationError = null;
      this.apiError = null;

      let inquiryIds;
      try {
        inquiryIds = this.parseInquiryIds();
      } catch (error) {
        this.validationError = error.message;
        return;
      }

      this.loading = true;
      try {
        const result = await this.api().request('POST', 'admin/scheduling-investigations', {
          inquiry_ids: inquiryIds,
        });
        this.result = result;
        this.selectedEventTypes = result.event_types.map((eventType) => eventType.value);
        this.expandedEventIds = [];
        this.inquiryIdsInput = inquiryIds.join(', ');
        const routeValue = inquiryIds.join(',');
        if (this.$route.params.inquiry_ids !== routeValue || this.$route.query.inquiry_ids) {
          await this.$router.replace({
            name: 'app.dev-tools.scheduling-investigation',
            params: { inquiry_ids: routeValue },
          });
        }
      } catch (error) {
        this.result = null;
        this.apiError = this.getFirstError(error) || error.message || 'An unexpected error occurred.';
      } finally {
        this.loading = false;
      }
    },
    selectAllEventTypes() {
      this.selectedEventTypes = this.result.event_types.map((eventType) => eventType.value);
    },
    clearEventTypes() {
      this.selectedEventTypes = [];
    },
    eventTypeLabel(type) {
      const eventType = this.result.event_types.find((item) => item.value === type);
      return eventType ? eventType.label : type;
    },
    toggleEvent(eventId) {
      if (this.isExpanded(eventId)) {
        this.expandedEventIds = this.expandedEventIds.filter((id) => id !== eventId);
      } else {
        this.expandedEventIds = [...this.expandedEventIds, eventId];
      }
    },
    isExpanded(eventId) {
      return this.expandedEventIds.includes(eventId);
    },
    eventSeverity(event) {
      if (event.type === 'access_code.recorded' && event.is_anomaly) return 'error';
      if (!this.result) return null;
      const diagnostic = this.result.diagnostics.find((item) => (
        item.entity_type === event.entity_type
        && item.entity_id === event.entity_id
      ));
      return diagnostic ? diagnostic.severity : null;
    },
    inquiryColorStyle(inquiryId) {
      const inquiryIndex = this.attributedInquiryIds.indexOf(inquiryId);
      const hue = Math.round((205 + Math.max(inquiryIndex, 0) * 137.508) % 360);

      return {
        '--inquiry-accent': `hsl(${hue}, 42%, 43%)`,
        '--inquiry-bg': `hsl(${hue}, 58%, 95%)`,
      };
    },
    eventInquiryStyle(event) {
      if (!event.inquiry_ids || event.inquiry_ids.length !== 1) return {};
      return this.inquiryColorStyle(event.inquiry_ids[0]);
    },
    buildMarkdownExport() {
      const exportedAt = new Date().toISOString()
        .replace('T', ' ')
        .replace(/\.\d{3}Z$/, ' UTC');
      const requestedIds = this.formatInquiryIdList(this.result.requested_inquiry_ids);
      const relatedIds = this.formatInquiryIdList(this.result.related_inquiry_ids);
      const missingIds = this.formatInquiryIdList(this.result.missing_inquiry_ids);
      const lines = [
        '# Scheduling Investigation',
        '',
        '> AI analysis prompt: Analyze this complete scheduling investigation. Reconstruct the causal sequence, identify failures or unexpected behavior, explain what most likely went wrong, and distinguish database evidence from inference. Pay particular attention to scheduling-attempt logs and Nylas free/busy responses, cancellations, rescheduling, notification failures, device access code failures, Showami errors, shared schedules, showing-window assignment strategies and assignee pools, legacy assignee mirror mismatches, and conflicting timestamps or flags.',
        '',
        '## Investigation scope',
        '',
        `- Exported at: ${exportedAt}`,
        '- Timestamp convention: Database UTC values copied without timezone conversion.',
        '- Export scope: All returned events are included, regardless of active timeline filters.',
        `- Requested inquiries: ${requestedIds}`,
        `- Related inquiries: ${relatedIds}`,
        `- Missing inquiries: ${missingIds}`,
        `- Total events: ${this.result.events.length}`,
        `- Anomalous events: ${this.result.events.filter((event) => event.is_anomaly).length}`,
        '',
        '## Diagnostics',
        '',
      ];

      if (this.result.diagnostics.length) {
        this.result.diagnostics.forEach((diagnostic) => {
          const entity = diagnostic.entity_type
            ? ` (${diagnostic.entity_type} #${diagnostic.entity_id})`
            : '';
          lines.push(`- **${diagnostic.severity.toUpperCase()} · ${diagnostic.type}**${entity}: ${diagnostic.summary}`);
        });
      } else {
        lines.push('- No diagnostics were detected.');
      }

      lines.push('', `## Timeline (${this.result.events.length} events)`, '');

      this.result.events.forEach((event) => {
        const entity = event.entity_id === null
          ? event.entity_type
          : `${event.entity_type} #${event.entity_id}`;
        lines.push(
          `### ${event.occurred_at} — ${event.summary}`,
          '',
          `- Event type: ${event.type} (${this.eventTypeLabel(event.type)})`,
          `- Entity: ${entity}`,
          `- Inquiry attribution: ${this.formatEventAttribution(event.inquiry_ids)}`,
          `- Failure or anomaly: ${event.is_anomaly ? 'Yes' : 'No'}`,
          `- Event ID: ${event.id}`,
          '',
          '**Event data**',
          '',
          this.markdownJsonBlock(event.details),
          '',
          '**Related context — current database state**',
          '',
          this.markdownJsonBlock(event.context),
          '',
        );
      });

      return lines.join('\n').trim();
    },
    formatInquiryIdList(inquiryIds) {
      if (!inquiryIds || !inquiryIds.length) return 'None';
      return inquiryIds.map((inquiryId) => `#${inquiryId}`).join(', ');
    },
    formatEventAttribution(inquiryIds) {
      if (!inquiryIds || !inquiryIds.length) return 'Unattributed';
      if (inquiryIds.length === 1) return `Inquiry #${inquiryIds[0]}`;
      return `Shared across ${inquiryIds.map((inquiryId) => `#${inquiryId}`).join(', ')}`;
    },
    markdownJsonBlock(value) {
      const json = JSON.stringify(value, null, 2);
      const backtickRuns = json.match(/`+/g) || [];
      const longestRun = backtickRuns.reduce((length, run) => Math.max(length, run.length), 0);
      const fence = '`'.repeat(Math.max(3, longestRun + 1));
      return `${fence}json\n${json}\n${fence}`;
    },
    async copyMarkdown() {
      const markdown = this.buildMarkdownExport();

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(markdown);
        } else {
          this.copyTextWithLegacyClipboard(markdown);
        }
        this.uiNotify(`Complete investigation copied as Markdown (${this.result.events.length} events).`);
      } catch (error) {
        this.uiNotify('Could not copy the investigation. Please try again.', 'negative');
      }
    },
    copyTextWithLegacyClipboard(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();

      let copied = false;
      try {
        copied = document.execCommand('copy');
      } finally {
        document.body.removeChild(textarea);
      }
      if (!copied) throw new Error('Clipboard copy failed.');
    },
    formatJson(value) {
      return JSON.stringify(value, null, 2);
    },
  },
};
</script>

<style lang="scss">
.scheduling-investigation-page {
  --ink: #17252c;
  --muted: #66757c;
  --line: #d8e0df;
  --paper: #f7f8f4;
  --signal: #e07a3f;
  --signal-soft: #fff0e6;
  color: var(--ink);
  max-width: 1440px;
  margin: 0 auto;

  .investigation-console {
    padding: 18px 20px 16px;
    background: #fff;
    border: 1px solid var(--line);
  }

  .section-label,
  .details-label {
    color: var(--signal);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.35px;
    text-transform: uppercase;
  }

  .input-hint {
    margin-top: 6px;
    color: var(--muted);
    font-size: 11px;
  }

  .investigate-button {
    min-height: 45px;
    box-shadow: none;
  }

  .missing-banner,
  .error-state {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 13px 16px;
    color: #7b421f;
    background: #fff5dc;
    border: 1px solid #efd39a;
  }

  .diagnostics-panel,
  .timeline-panel {
    background: #fff;
    border: 1px solid var(--line);
  }

  .diagnostics-panel {
    padding: 17px 19px 19px;
  }

  .diagnostic-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: 10px;
  }

  .diagnostic-card {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    font-size: 12px;
    border-left: 3px solid;

    &.diagnostic-warning {
      color: #7b421f;
      background: #fff7e7;
      border-color: #d59646;
    }

    &.diagnostic-error {
      color: #843526;
      background: #fff0ec;
      border-color: #c6543c;
    }
  }

  .timeline-toolbar {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    padding: 18px 20px 14px;
    background: var(--paper);
    border-bottom: 1px solid var(--line);
  }

  .timeline-count {
    margin-top: 4px;
    color: var(--muted);
    font-size: 12px;
  }

  .inquiry-key {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
    margin-top: 10px;
  }

  .inquiry-key-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #56676c;
    font-size: 11px;
    font-weight: 600;

    i {
      width: 9px;
      height: 9px;
      background: var(--inquiry-bg);
      border: 2px solid var(--inquiry-accent);
      border-radius: 50%;
    }

    .shared-key {
      background: #f3f5f4;
      border-color: #9aa7aa;
    }
  }

  .filter-control {
    width: 100%;
  }

  .timeline-options {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
    min-height: 28px;
    color: #5e6e73;
    font-size: 12px;
  }

  .copy-markdown-button {
    color: #326377;
    font-size: 11px;
  }

  .filter-actions {
    margin-top: 3px;
    text-align: right;
    color: #9aa4a7;
    font-size: 11px;

    button {
      padding: 0 4px;
      color: #326377;
      background: transparent;
      border: 0;
      cursor: pointer;
    }
  }

  .event-list {
    position: relative;
    padding: 7px 0;
  }

  .event-row {
    position: relative;
    border-bottom: 1px solid #edf0ee;

    &:last-child {
      border-bottom: 0;
    }

    &.is-inquiry-specific {
      background: var(--inquiry-bg);

      .event-marker {
        background: var(--inquiry-accent);
        border-color: var(--inquiry-accent);
      }
    }

    &.event-error {
      background: #fff4f1;
    }

    &.event-warning {
      background: #fff9eb;
    }

    &.is-expanded:not(.is-inquiry-specific):not(.event-error):not(.event-warning) {
      background: #f8faf8;
    }
  }

  .event-header {
    display: grid;
    grid-template-columns: 19px 154px 14px minmax(0, 1fr) auto 22px;
    align-items: center;
    width: 100%;
    min-height: 51px;
    padding: 7px 18px 7px 15px;
    color: inherit;
    text-align: left;
    background: transparent;
    border: 0;
    cursor: pointer;

    &:hover {
      background: rgba(33, 76, 87, 0.035);
    }
  }

  .event-marker {
    position: relative;
    z-index: 1;
    width: 9px;
    height: 9px;
    background: #fff;
    border: 2px solid #46717b;
    border-radius: 50%;
  }

  .event-notification-sent .event-marker {
    border-color: #5b879f;
  }

  .event-showami-log .event-marker {
    border-color: var(--signal);
  }

  .event-access_code-recorded .event-marker {
    border-color: #7763a6;
  }

  .event-time {
    color: #294e58;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
  }

  .event-separator {
    color: #9aa7aa;
  }

  .event-summary {
    overflow: hidden;
    padding-right: 14px;
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .event-type {
    padding: 3px 7px;
    color: #5f6f74;
    background: #eef2f1;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.25px;
    white-space: nowrap;
  }

  .event-toggle {
    color: #78878b;
    justify-self: end;
  }

  .event-details {
    display: grid;
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    gap: 12px;
    padding: 2px 18px 18px 48px;
  }

  .details-column {
    min-width: 0;
  }

  .details-label {
    margin-bottom: 6px;
    color: #65767b;
  }

  pre {
    overflow: auto;
    max-height: 420px;
    margin: 0;
    padding: 13px 15px;
    color: #dce7e5;
    background: #17282e;
    border-left: 3px solid #426d77;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11px;
    line-height: 1.55;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .empty-state,
  .initial-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 260px;
    padding: 36px;
    color: var(--muted);
    text-align: center;

    strong {
      margin: 12px 0 5px;
      color: var(--ink);
      font-size: 16px;
    }

    span {
      max-width: 620px;
      font-size: 12px;
    }
  }

  .initial-state {
    min-height: 310px;
    background:
      radial-gradient(circle at center, rgba(39, 90, 102, 0.06), transparent 48%),
      #fff;
    border: 1px dashed #c6d0ce;
  }

  .initial-orbit {
    display: grid;
    width: 62px;
    height: 62px;
    color: #3b6974;
    border: 1px solid #9fb4b5;
    border-radius: 50%;
    place-items: center;
    box-shadow: 0 0 0 10px rgba(63, 107, 116, 0.06);

    .q-icon {
      font-size: 28px;
    }
  }

  .error-state {
    align-items: flex-start;
    margin-top: 14px;
    background: #fff0ec;
    border-color: #efb7aa;

    .q-icon {
      font-size: 24px;
    }

    p {
      margin: 3px 0 0;
    }
  }
}

@media (max-width: 900px) {
  .scheduling-investigation-page {
    .timeline-toolbar {
      flex-direction: column;
    }

    .timeline-options {
      align-items: flex-start;
      flex-direction: column;
      gap: 4px;
    }

    .diagnostic-grid,
    .event-details {
      grid-template-columns: 1fr;
    }

    .event-header {
      grid-template-columns: 19px minmax(0, 1fr) 22px;
      gap: 2px 0;
    }

    .event-time,
    .event-summary {
      grid-column: 2;
    }

    .event-separator,
    .event-type {
      display: none;
    }

    .event-toggle {
      grid-column: 3;
      grid-row: 1 / 3;
    }

    .event-details {
      padding-left: 34px;
    }
  }
}
</style>
